import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: '每日大赛Yandex',
    },
    publisher: {
      '@type': 'Organization',
      name: '每日大赛Yandex',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yandex-contest.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '分类',
        item: `https://yandex-contest.com/category/${article.categorySlug}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || '分类', href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8">
            <article className="flex-1 min-w-0">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                    {category?.name}
                  </span>
                  <span className="text-xs text-foreground-400">{article.publishDate}</span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读约{article.readTime}分钟
                  </span>
                </div>

                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  {article.title}
                </h1>

                <div
                  className="prose prose-sm max-w-none text-foreground-700"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>
            </article>

            <aside className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4 font-heading">
                    相关文章
                  </h3>
                  {relatedArticles.length > 0 ? (
                    <div className="space-y-3">
                      {relatedArticles.map((related) => (
                        <Link
                          key={related.slug}
                          to={`/article/${related.slug}`}
                          className="block p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 hover:text-primary-600 transition-colors">
                            {related.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-foreground-400">{related.publishDate}</span>
                            <span className="text-xs text-foreground-400">·</span>
                            <span className="text-xs text-foreground-400">阅读{related.readTime}分钟</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-foreground-500">暂无相关文章</p>
                  )}

                  <div className="mt-5 pt-5 border-t border-background-200/70">
                    <Link
                      to={`/category/${article.categorySlug}`}
                      className="flex items-center justify-between text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      <span>返回{category?.name}</span>
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}